import { useRef, useMemo, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useStore } from "@/store/useStore";

/* ---------- AirPods: stylized ear + spatial audio visualizer ---------- */
export function EarRig() {
  const group = useRef();
  const bars = useRef([]);
  const eq = useStore((s) => s.eq);
  const spatial = useStore((s) => s.spatialAudio);
  const select = useStore((s) => s.airpodSelect);
  const playing = useStore((s) => s.playing);

  const count = 28;
  const ring = useMemo(() => new Array(count).fill(0).map((_, i) => i), []);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const amp = (eq.bass + eq.mid + eq.treble) / 300; // 0..1-ish
    bars.current.forEach((m, i) => {
      if (!m) return;
      const base = 0.2 + 0.8 * Math.abs(Math.sin(t * 3 + i * 0.5));
      const h = 0.15 + base * amp * (playing ? 1.6 : 0.5) + (spatial ? 0.08 : 0);
      m.scale.y = THREE.MathUtils.lerp(m.scale.y, h, 0.25);
      m.position.y = -0.9 + m.scale.y / 2;
    });
    if (group.current) group.current.rotation.y = t * (spatial ? 0.4 : 0.15);
  });

  const earColor = select === "left" ? "#00f0ff" : select === "right" ? "#a855f7" : "#8be9ff";

  return (
    <group position={[2.7, 0, 0]}>
      {/* translucent ear silhouette */}
      <group rotation={[0, -0.3, 0]}>
        <mesh>
          <torusGeometry args={[0.55, 0.16, 20, 40]} />
          <meshPhysicalMaterial
            color={earColor}
            transparent
            opacity={0.28}
            roughness={0.2}
            transmission={0.6}
            thickness={0.5}
            emissive={earColor}
            emissiveIntensity={0.25}
          />
        </mesh>
        <mesh position={[0.05, -0.1, 0]} scale={[0.6, 0.8, 0.6]}>
          <sphereGeometry args={[0.4, 24, 24]} />
          <meshPhysicalMaterial color={earColor} transparent opacity={0.16} roughness={0.3} />
        </mesh>
      </group>

      {/* spatial audio visualizer ring */}
      <group ref={group} position={[0, 0, 0]}>
        {ring.map((i) => {
          const a = (i / count) * Math.PI * 2;
          const r = 1.25;
          return (
            <mesh
              key={i}
              ref={(el) => (bars.current[i] = el)}
              position={[Math.cos(a) * r, 0, Math.sin(a) * r]}
            >
              <boxGeometry args={[0.06, 0.4, 0.06]} />
              <meshStandardMaterial
                color={i % 2 ? "#00f0ff" : "#a855f7"}
                emissive={i % 2 ? "#00f0ff" : "#a855f7"}
                emissiveIntensity={0.7}
              />
            </mesh>
          );
        })}
      </group>
    </group>
  );
}

/* ---------- Lotus Ring: procedural hand + smart-home gesture glow ---------- */
export function HandRig() {
  const flashlight = useStore((s) => s.flashlight);
  const tv = useStore((s) => s.tv);
  const lock = useStore((s) => s.lock);
  const variant = useStore((s) => s.variant);
  const skinTone = useStore((s) => s.skinTone);
  const icon = useRef();

  const anyOn = flashlight || tv || lock;

  const ringMat =
    {
      graphite: { color: "#3a3d44", metalness: 1, roughness: 0.34 },
      rosegold: { color: "#e3a593", metalness: 1, roughness: 0.28 },
      silver: { color: "#d7dbe0", metalness: 1, roughness: 0.2 },
    }[variant] || { color: "#3a3d44", metalness: 1, roughness: 0.34 };

  const skin =
    { light: "#ecc6a6", medium: "#cf9d74", tan: "#a9774f", deep: "#6d4733" }[skinTone] || "#cf9d74";

  useFrame((state) => {
    if (icon.current) {
      const t = state.clock.elapsedTime;
      icon.current.material.emissiveIntensity = anyOn ? 1.2 + Math.sin(t * 6) * 0.5 : 0;
      icon.current.scale.setScalar(anyOn ? 0.26 + Math.sin(t * 6) * 0.03 : 0.0001);
    }
  });

  const gColor = flashlight ? "#ffd166" : tv ? "#00f0ff" : "#a855f7";

  return (
    <group position={[1.2, -0.15, 0]} rotation={[0.42, -0.5, 0.06]} scale={1.05}>
      <RealHand skin={skin} ringMat={ringMat} ringEmissive={anyOn ? gColor : null} />

      {/* floating gesture action glow */}
      <mesh ref={icon} position={[0.1, 1.95, 0]}>
        <icosahedronGeometry args={[1, 1]} />
        <meshStandardMaterial color={gColor} emissive={gColor} emissiveIntensity={1} />
      </mesh>

      {flashlight && <FlashlightBeam />}
    </group>
  );
}

const SkinMat = ({ skin }) => (
  <meshPhysicalMaterial
    color={skin}
    roughness={0.6}
    clearcoat={0.18}
    clearcoatRoughness={0.6}
    sheen={0.5}
    sheenColor={"#ffcbb0"}
    sheenRoughness={0.7}
  />
);

function Finger({ skin, base, fan = 0, baseCurl = 0.12, lengths = [0.32, 0.25, 0.19], radius = 0.085, ring = false, ringMat, ringEmissive }) {
  const r2 = radius * 0.9;
  const r3 = radius * 0.8;
  return (
    <group position={base} rotation={[baseCurl, 0, fan]}>
      {/* knuckle */}
      <mesh>
        <sphereGeometry args={[radius * 1.02, 20, 20]} />
        <SkinMat skin={skin} />
      </mesh>
      {/* proximal segment */}
      <mesh position={[0, lengths[0] / 2, 0]}>
        <capsuleGeometry args={[radius, lengths[0], 8, 16]} />
        <SkinMat skin={skin} />
      </mesh>

      {ring && (
        <mesh position={[0, lengths[0] * 0.5, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[radius + 0.03, 0.032, 24, 48]} />
          <meshStandardMaterial
            color={ringMat.color}
            metalness={ringMat.metalness}
            roughness={ringMat.roughness}
            emissive={ringEmissive || "#000000"}
            emissiveIntensity={ringEmissive ? 0.5 : 0}
          />
        </mesh>
      )}

      {/* middle segment */}
      <group position={[0, lengths[0], 0]} rotation={[0.34, 0, 0]}>
        <mesh>
          <sphereGeometry args={[r2, 16, 16]} />
          <SkinMat skin={skin} />
        </mesh>
        <mesh position={[0, lengths[1] / 2, 0]}>
          <capsuleGeometry args={[r2, lengths[1], 8, 16]} />
          <SkinMat skin={skin} />
        </mesh>
        {/* distal segment */}
        <group position={[0, lengths[1], 0]} rotation={[0.4, 0, 0]}>
          <mesh>
            <sphereGeometry args={[r3, 14, 14]} />
            <SkinMat skin={skin} />
          </mesh>
          <mesh position={[0, lengths[2] / 2, 0]}>
            <capsuleGeometry args={[r3, lengths[2], 8, 16]} />
            <SkinMat skin={skin} />
          </mesh>
          <mesh position={[0, lengths[2] + r3 * 0.4, 0]}>
            <sphereGeometry args={[r3 * 0.92, 14, 14]} />
            <SkinMat skin={skin} />
          </mesh>
        </group>
      </group>
    </group>
  );
}

function RealHand({ skin, ringMat, ringEmissive }) {
  return (
    <group>
      {/* palm (flattened oval) */}
      <mesh position={[0, 0.12, 0]} scale={[0.96, 0.52, 1.12]}>
        <sphereGeometry args={[0.5, 40, 40]} />
        <SkinMat skin={skin} />
      </mesh>
      {/* lower palm */}
      <mesh position={[0, -0.26, -0.01]} scale={[0.82, 0.6, 0.98]}>
        <sphereGeometry args={[0.42, 28, 28]} />
        <SkinMat skin={skin} />
      </mesh>
      {/* wrist */}
      <mesh position={[0, -0.64, -0.02]} rotation={[0.08, 0, 0]}>
        <cylinderGeometry args={[0.32, 0.36, 0.5, 28]} />
        <SkinMat skin={skin} />
      </mesh>
      {/* thenar mound (thumb base) */}
      <mesh position={[-0.33, -0.04, 0.12]} scale={[0.62, 0.72, 0.7]}>
        <sphereGeometry args={[0.3, 24, 24]} />
        <SkinMat skin={skin} />
      </mesh>

      {/* four fingers (index, middle, ring[+band], pinky) */}
      <Finger skin={skin} base={[-0.34, 0.44, 0.02]} fan={0.17} baseCurl={0.12} lengths={[0.3, 0.24, 0.18]} radius={0.082} />
      <Finger skin={skin} base={[-0.12, 0.5, 0.03]} fan={0.05} baseCurl={0.1} lengths={[0.34, 0.27, 0.2]} radius={0.087} />
      <Finger skin={skin} base={[0.12, 0.48, 0.03]} fan={-0.05} baseCurl={0.1} lengths={[0.31, 0.25, 0.19]} radius={0.085} ring ringMat={ringMat} ringEmissive={ringEmissive} />
      <Finger skin={skin} base={[0.33, 0.4, 0.02]} fan={-0.21} baseCurl={0.14} lengths={[0.24, 0.19, 0.15]} radius={0.072} />

      {/* thumb */}
      <group position={[-0.42, -0.02, 0.14]} rotation={[0.2, 0.3, 0.98]}>
        <mesh>
          <sphereGeometry args={[0.1, 18, 18]} />
          <SkinMat skin={skin} />
        </mesh>
        <mesh position={[0, 0.16, 0]}>
          <capsuleGeometry args={[0.094, 0.26, 8, 16]} />
          <SkinMat skin={skin} />
        </mesh>
        <group position={[0, 0.34, 0]} rotation={[0.4, 0, 0]}>
          <mesh position={[0, 0.13, 0]}>
            <capsuleGeometry args={[0.082, 0.22, 8, 16]} />
            <SkinMat skin={skin} />
          </mesh>
          <mesh position={[0, 0.28, 0]}>
            <sphereGeometry args={[0.078, 14, 14]} />
            <SkinMat skin={skin} />
          </mesh>
        </group>
      </group>
    </group>
  );
}

function FlashlightBeam() {
  const spot = useRef();
  const tgt = useRef();
  useEffect(() => {
    if (spot.current && tgt.current) {
      spot.current.target = tgt.current;
      spot.current.target.updateMatrixWorld();
    }
  }, []);
  return (
    <group position={[0.12, 0.64, 0.12]}>
      <spotLight
        ref={spot}
        position={[0, 0, 0]}
        color="#ffffff"
        intensity={90}
        distance={20}
        angle={0.6}
        penumbra={0.6}
        decay={1}
      />
      <object3D ref={tgt} position={[0.3, 0.5, 5]} />
      {/* visible volumetric beam */}
      <mesh position={[0.12, 0.2, 2.1]} rotation={[-Math.PI / 2 + 0.1, 0, 0]}>
        <coneGeometry args={[1.5, 4.2, 36, 1, true]} />
        <meshBasicMaterial
          color="#fff6d8"
          transparent
          opacity={0.12}
          side={THREE.DoubleSide}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
      {/* bright source dot */}
      <mesh>
        <sphereGeometry args={[0.06, 16, 16]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
    </group>
  );
}

/* ---------- L'Oréal Melasyl: 3D skin surface w/ pigmentation zones ---------- */
export function SkinSurface() {
  const intensity = useStore((s) => s.skinIntensity);
  const fitz = useStore((s) => s.fitz);
  const spots = useRef([]);

  const fitzColors = ["#f3d4c0", "#ecc3a6", "#d9a67e", "#bf8560", "#9c6442", "#6f4328"];
  const baseColor = fitzColors[Math.min(5, Math.max(0, fitz - 1))];

  const zones = useMemo(
    () =>
      new Array(14).fill(0).map(() => ({
        x: (Math.random() - 0.5) * 2.4,
        y: (Math.random() - 0.5) * 2.4,
        s: 0.1 + Math.random() * 0.22,
      })),
    []
  );

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const k = intensity / 100;
    spots.current.forEach((m, i) => {
      if (!m) return;
      const pulse = 0.6 + Math.sin(t * 2 + i) * 0.4;
      m.material.opacity = THREE.MathUtils.lerp(m.material.opacity, k * pulse, 0.1);
    });
  });

  return (
    <group position={[2.8, 0, 0]} rotation={[0, -0.5, 0]}>
      {/* skin plate */}
      <mesh>
        <boxGeometry args={[2.8, 2.8, 0.14]} />
        <meshPhysicalMaterial color={baseColor} roughness={0.55} clearcoat={0.4} clearcoatRoughness={0.6} />
      </mesh>
      {/* pigmentation response zones */}
      {zones.map((z, i) => (
        <mesh key={i} position={[z.x, z.y, 0.09]} ref={(el) => (spots.current[i] = el)}>
          <circleGeometry args={[z.s, 24]} />
          <meshBasicMaterial color={"#5a2c1a"} transparent opacity={0.1} />
        </mesh>
      ))}
    </group>
  );
}
